import { createContext, useContext } from 'react';
import { runInAction, makeObservable, action, computed, observable } from 'mobx';
import IUWProductsPage from 'store/modules/uwProducts/iUWProductsPage';
import UWProductsModule, { AttributeInfo } from 'store/modules/uwProducts/uwProductsModule';
import { PlanAttributeType, ProductTemplate, ProductTemplateChecklistCard } from 'types/productServiceV2';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { updateProductTemplate } from 'api/operations/productServiceV2';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';

interface UpdateTemplateFields extends DefaultFormFields {
  name: FormControl<string>;
  internalName: FormControl<string>;
  productLineId: FormControl<string>;
  state: FormControl<string>;
  effectiveFrom: FormControl<string>;
  effectiveTo: FormControl<string>;
}

export const defaultSaveProductForm = new FormController({
  name: new FormControl<string>('', { optional: false, trim: true }),
  internalName: new FormControl<string>('', { optional: false, trim: true }),
  productLineId: new FormControl<string>('CustomDental', { optional: false }),
  state: new FormControl<string>('', { optional: false }),
  effectiveFrom: new FormControl<string>('', { optional: false }),
  effectiveTo: new FormControl<string>('', { optional: false }),
});

export default class RFQSaveProductPageStore extends IUWProductsPage {
  uwProductsModule: UWProductsModule;

  private _isLoading = false;
  private _productTemplate: ProductTemplate | null = null;
  private _requiredAttributes: AttributeInfo[] = [];
  private _customAttributes: AttributeInfo[] = [];
  private _updateTemplateForm: FormController<UpdateTemplateFields>;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  protected get productTemplate(): ProductTemplate | null {
    return this._productTemplate;
  }

  public get requiredAttributes(): AttributeInfo[] {
    return this._requiredAttributes;
  }

  public get customAttributes(): AttributeInfo[] {
    return this._customAttributes;
  }

  public get updateTemplateForm(): FormController<UpdateTemplateFields> {
    return this._updateTemplateForm;
  }

  public get availableProductLinesOptions(): OptionType[] {
    const pLineId = this._productTemplate?.productLineId || 'CustomDental';

    return [
      {
        value: pLineId,
        label: PRODUCT_LINES_CONTENT_MAP[pLineId]?.label,
      },
    ];
  }

  public get availableStatesOptions(): OptionType[] {
    const states = this._productTemplate?.filterBC?.state;
    return [
      {
        value: states ? states[0] : '',
        label: states ? states[0] : '',
      },
    ];
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  // Api calls
  public async getInitialData(templateId: string): Promise<void> {
    this.setIsLoading(true);
    // Common initial requests from module
    await this.uwProductsModule.getInitialData(templateId);

    void runInAction(() => {
      // Set data from module
      this._productTemplate = this.uwProductsModule.productTemplate;
      this._requiredAttributes = this.uwProductsModule.requiredAttributes;
      this._customAttributes = this.uwProductsModule.customAttributes;

      // Specific step request
      if (this._productTemplate) {
        runInAction(() => {
          this.populateFilledTemplateInfo(this._productTemplate!);
          this.setIsLoading(false);
        });
      }
    });
  }

  public async completeStep(): Promise<void> {
    this.setIsLoading(true);
    const { name, internalName, productLineId, effectiveFrom, effectiveTo, state } = this.updateTemplateForm.fields;

    await updateProductTemplate({
      id: this.productTemplate?.id || '',
      name: name.value,
      internalName: internalName.value,
      productLineId: productLineId.value as ProductLineId,
      effectiveFrom: effectiveFrom.value,
      effectiveTo: effectiveTo.value,
      state: state.value,
    });

    await this.uwProductsModule.onCompleteStep(ProductTemplateChecklistCard.SAVE_PRODUCT_TEMPLATE);
    this.setIsLoading(false);
  }

  // Helpers
  private populateFilledTemplateInfo(template: ProductTemplate): void {
    const { name, internalName, productLineId, filterBC, effectiveFrom, effectiveTo } = template;
    this.updateTemplateForm.populateFields({
      name,
      internalName: internalName || name,
      productLineId,
      state: filterBC?.state ? filterBC.state[0] : '',
      effectiveFrom,
      effectiveTo,
    });
  }

  public resetInitialData(): void {
    this._productTemplate = null;
    this._requiredAttributes = [];
    this._customAttributes = [];
  }

  public getAttributesQuantityByType(planType: PlanAttributeType): number {
    const totalCountOfFilledAttributes = [...this.requiredAttributes, ...this.customAttributes].reduce(
      (count, { engValues }) => {
        if (planType === 'FILLED_COVERED') {
          return count + (engValues && engValues.length > 0 ? 1 : 0);
        }
        if (planType === 'FILLED_UNCOVERED') {
          return count + (engValues && engValues.length === 0 ? 1 : 0);
        }
        return count + (!engValues ? 1 : 0);
      },
      0,
    );

    return totalCountOfFilledAttributes;
  }

  public get isStepCompleted(): boolean {
    return this.uwProductsModule.isStepCompleted(ProductTemplateChecklistCard.SAVE_PRODUCT_TEMPLATE);
  }

  public get isTemplateInProgress(): boolean {
    return this.uwProductsModule.isTemplateInProgress;
  }

  constructor() {
    super();
    this.uwProductsModule = new UWProductsModule();
    this._updateTemplateForm = defaultSaveProductForm;

    makeObservable<
      RFQSaveProductPageStore,
      // Private observable properties and actions
      | '_productTemplate'
      | '_requiredAttributes'
      | '_customAttributes'
      | '_isLoading'
      | '_updateTemplateForm'
      | 'setIsLoading'
    >(this, {
      // Private properties
      _productTemplate: observable,
      _requiredAttributes: observable,
      _customAttributes: observable,
      _isLoading: observable,
      _updateTemplateForm: observable,

      // Getters
      isLoading: computed,
      requiredAttributes: computed,
      customAttributes: computed,
      updateTemplateForm: computed,
      availableProductLinesOptions: computed,
      isStepCompleted: computed,
      isTemplateInProgress: computed,

      // Setters
      setIsLoading: action.bound,

      // Other actions
      getInitialData: action.bound,
      completeStep: action.bound,
      resetInitialData: action.bound,
      getAttributesQuantityByType: action.bound,
    });
  }
}

const PageStoreContext = createContext(new RFQSaveProductPageStore());

export const useRFQSaveProductPageStore = (): RFQSaveProductPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
